import store from "../../store";
import {setLastFlush} from "../../actions/metrics";

/** Umbral de flush: enviar POST cuando haya esta cantidad de eventos en cola. */
var FLUSH_THRESHOLD = 10;

/** Intervalo del temporizador en milisegundos antes de vaciar una cola pendiente. */
var FLUSH_INTERVAL_MS = 5000;

/**
 * Retrasos de reintento en milisegundos para POST de lotes fallidos.
 * Índice 0 → primer reintento, índice 1 → segundo reintento, índice 2 → tercer reintento.
 */
var RETRY_DELAYS_MS = [1000, 2000, 4000];

class NewRelicEventAPI {

    constructor() {
        this._accountId = process.env.NEWRELIC_ACCOUNT_ID;   // ← Reemplazar con el Account ID real
        this._insertKey = process.env.NEWRELIC_INSERT_KEY;   // ← Reemplazar con la Insert API Key real
        this._endpoint = 'https://insights-collector.newrelic.com/v1/accounts/' + this._accountId + '/events';
        this._globalAttributes = {};

        /** @type {Array<object>} Cola de eventos en memoria. */
        this._queue = [];

        /** @type {number|null} Referencia activa del temporizador de flush. */
        this._flushTimer = null;

        /** @type {boolean} Evita flushes concurrentes. */
        this._isFlushing = false;
    }


    /**
     * Construye un objeto de evento, lo agrega a la cola interna y dispara un
     * flush por umbral cuando la cola alcanza FLUSH_THRESHOLD. También programa
     * un flush por temporizador si aún no hay uno pendiente.
     *
     * La lógica de construcción del evento es igual a la implementación previa
     * de envío inmediato para que todos los puntos de llamada existentes sigan siendo compatibles.
     */
    sendEvent(eventType, attributes) {
        var event = {
            eventType: eventType,
            timestamp: Date.now()
        };

        // Combinar atributos globales (userId, appVersion, currentRoute)
        var globalKeys = Object.keys(this._globalAttributes);
        for (var i = 0; i < globalKeys.length; i++) {
            var key = globalKeys[i];
            event[key] = this._globalAttributes[key];
        }

        // Combinar atributos específicos del evento
        var attrKeys = Object.keys(attributes);
        for (var j = 0; j < attrKeys.length; j++) {
            var attrKey = attrKeys[j];
            var value = attributes[attrKey];
            // Truncar valores string > 4096 caracteres (límite de Event API)
            if (typeof value === 'string' && value.length > 4096) {
                event[attrKey] = value.substring(0, 4093) + '...';
            } else {
                event[attrKey] = value;
            }
        }

        this._queue.push(event);

        if (this._queue.length >= FLUSH_THRESHOLD) {
            this._cancelFlushTimer();
            this.flush();
        } else {
            this._ensureFlushTimer();
        }
    }


    /**
     * Programa un flush por temporizador si aún no hay uno activo.
     * @private
     */
    _ensureFlushTimer() {
        if (this._flushTimer !== null) return;
        var self = this;
        this._flushTimer = setTimeout(function () {
            self._flushTimer = null;
            self.flush();
        }, FLUSH_INTERVAL_MS);
    }


    /**
     * Cancela cualquier temporizador de flush pendiente.
     * @private
     */
    _cancelFlushTimer() {
        if (this._flushTimer !== null) {
            clearTimeout(this._flushTimer);
            this._flushTimer = null;
        }
    }


    /**
     * Vacía la cola actual y envía todos los eventos acumulados en un solo POST
     * por lote. Si la cola está vacía, no hace nada.
     *
     * Usa una bandera de control para evitar flushes superpuestos cuando la
     * lógica de reintento coincide con el temporizador.
     */
    flush() {
        if (this._queue.length === 0) return;
        if (this._isFlushing) return;

        var batch = this._queue.slice();
        this._queue = [];
        this._isFlushing = true;

        var self = this;
        this._sendBatch(batch, 0, function () {
            self._isFlushing = false;
        });

        store.dispatch(setLastFlush(Date.now()));
    }


    /**
     * Envía un lote de eventos mediante un único POST por XHR.
     * Si falla, reintenta hasta RETRY_DELAYS_MS.length veces usando el retraso
     * fijo correspondiente y luego descarta el lote.
     *
     * @param {Array<object>} batch    Eventos a enviar.
     * @param {number}        retryIdx Índice del intento actual (0 = envío inicial).
     * @param {Function}      done     Se invoca cuando el lote termina (éxito o agotado).
     * @private
     */
    _sendBatch(batch, retryIdx, done) {
        var self = this;
        var payload = JSON.stringify(batch);

        var xhr = new XMLHttpRequest();
        xhr.open('POST', this._endpoint);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Api-Key', this._insertKey);

        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) return;

            if (xhr.status === 200) {
                // Entregado correctamente
                done();
                return;
            }

            // Falló: reintentar si aún quedan intentos
            if (retryIdx < RETRY_DELAYS_MS.length) {
                var delay = RETRY_DELAYS_MS[retryIdx];
                setTimeout(function () {
                    self._sendBatch(batch, retryIdx + 1, done);
                }, delay);
            } else {
                // Reintentos agotados: descartar el lote
                console.error('[NewRelicEventAPI] Batch discarded after ' + RETRY_DELAYS_MS.length + ' retries. Status:', xhr.status);
                done();
            }
        };

        xhr.onerror = function () {
            if (retryIdx < RETRY_DELAYS_MS.length) {
                var delay = RETRY_DELAYS_MS[retryIdx];
                setTimeout(function () {
                    self._sendBatch(batch, retryIdx + 1, done);
                }, delay);
            } else {
                console.error('[NewRelicEventAPI] Network error. Batch discarded after ' + RETRY_DELAYS_MS.length + ' retries.');
                done();
            }
        };

        try {
            xhr.send(payload);
        } catch (e) {
            console.error('[NewRelicEventAPI] xhr.send threw synchronously', e);
            done();
        }
    }


    /**
     * Establece un atributo que se adjuntará a TODOS los eventos futuros.
     * Lo usan los llamados de setUserId, setApplicationVersion y setCurrentRouteName.
     */
    setGlobalAttribute(name, value) {
        this._globalAttributes[name] = value;
    }


    /**
     * Cancela el temporizador de flush, limpia los atributos globales y descarta
     * cualquier evento en cola que todavía no se haya enviado.
     *
     * Llamar cuando la app se cierre o cambie de contexto.
     */
    destroy() {
        this._cancelFlushTimer();
        this._globalAttributes = {};
        this._queue = [];
        this._isFlushing = false;
    }
}

export default NewRelicEventAPI;



// WEBPACK FOOTER //
// ./src/utils/trackers/NewRelicEventAPI.js